// Types mirror the extend-api card-feeds DTOs (CardFeedResponse,
// CardFeedEntryResponse). See organizations-card-feeds/libs/schema in
// extend-api. NOTE: the card-feeds API returns its own pagination shape
// ({ page, pageSize, totalPages }) which differs from the app-wide
// PaginationResponse — keep this local type, do not swap in the shared one.

export type CardNetwork = 'VISA' | 'MASTERCARD' | 'AMERICAN_EXPRESS'

// Mirrors the extend-api CardFeedStatus enum (organizations-card-feeds:
// commands/CardFeed.kt). Wire-aligned with the integrator (Astrada) lifecycle:
// SUBMITTED (feed created, awaiting activation) → ACTIVE (connected) and the
// FAILED / DEACTIVATED terminal states.
export type CardFeedStatus = 'SUBMITTED' | 'ACTIVE' | 'FAILED' | 'DEACTIVATED'

export type CardFeedCardStatus = 'ASSIGNED' | 'UNASSIGNED' | 'PENDING'

export interface CardFeedPagination {
  page: number
  pageSize: number
  totalPages: number
}

export interface CardFeedResponse {
  id: string
  organizationId: string
  cardProgramName: string
  cardNetwork: CardNetwork
  integratorFeedId: string
  // Omitted by the API until the count is available.
  cardCount?: number | null
  status: CardFeedStatus
  // Omitted by the API until the feed is connected (e.g. PENDING feeds).
  referenceId?: string | null
  createdAt: string
  updatedAt: string
}

export interface CardFeedsResponse {
  cardFeeds: CardFeedResponse[]
  pagination: CardFeedPagination
}

// The API serializes the full User object for an assigned cardholder; we only
// read identity fields, so keep a minimal structural type rather than coupling
// to the heavy app-wide User model.
export interface CardFeedCardholder {
  id?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
}

export interface CardFeedEntryResponse {
  id: string
  displayName: string
  last4: string
  // Omitted by the API until a cardholder is assigned.
  cardholder?: CardFeedCardholder | null
  status: CardFeedCardStatus
  createdAt: string
  updatedAt: string
}

export interface CardFeedEntriesResponse {
  creditCards: CardFeedEntryResponse[]
  pagination: CardFeedPagination
}

// View model consumed by CardFeedCardsTable. Maps API field names to the
// names the table renders, and flattens the cardholder to a display string.
export interface CardFeedCard {
  id: string
  cardName: string
  lastFour: string
  cardHolder: string | null
  status: CardFeedCardStatus
  dateAdded: string
}

export const cardholderDisplayName = (
  cardholder: CardFeedCardholder | null | undefined,
): string | null => {
  if (!cardholder) return null
  const fullName = [cardholder.firstName, cardholder.lastName]
    .filter((part) => !!part)
    .join(' ')
    .trim()
  return fullName || cardholder.email || null
}

export const toCardFeedCard = (entry: CardFeedEntryResponse): CardFeedCard => ({
  id: entry.id,
  cardName: entry.displayName,
  lastFour: entry.last4,
  cardHolder: cardholderDisplayName(entry.cardholder),
  status: entry.status,
  dateAdded: entry.createdAt,
})

// Mirrors AssignCardFeedCardsRequest / AssignCardFeedCardsResponse in
// extend-api (organizations-card-feeds/libs/schema). The endpoint resolves the
// assignee from the email — self, existing member (promoted to CARD_MANAGER),
// or a brand-new invite — and grants Card Manager access to the listed cards.
export interface AssignCardFeedCardsRequest {
  email: string
  creditCardIds: string[]
}

export interface AssignCardFeedCardsResponse {
  message: string
  assignedCount: number
}

export type CreateCardFeedRequest =
  | {
      cardNetwork: 'MASTERCARD'
      deliveryId: string
    }
  | {
      cardNetwork: 'VISA'
      regionId: string
      processorId: string
      issuerId: string
      companyId: string
    }
